import gql from 'graphql-tag';
import React, { Fragment, useCallback, useEffect, useState } from 'react';
import { Query } from 'react-apollo';
import { Box, Button, Card, Flex, Heading, Text } from 'rebass';
import PleaseWait from '../../../components/PleaseWait';
import { isPreviewMode } from '../../../utils/environments';
import styled from 'styled-components';

const Container = styled(Box)`
  width: 100%;
  margin: 0 auto;
  padding: 20px 16px 40px 16px;
  min-height: 80vh;
  max-height: 95vh;
  overflow-y: auto;
  @media screen and (max-width: 52em) {
    padding-left: 8px;
    padding-right: 8px;
  }
`;
const GET_BRIDGE_DATA = gql`
  query getUserBridgeData($userId: String!) {
    getUserBridgeData(userId: $userId) {
      bridgePatient {
        id
        createdAt
        email
        patientToken
        externalId
        phone
        address {
          state
        }
        account {
          status
          balance
        }
      }
      policy {
        latest {
          effectiveTo
          effectiveFrom
          validatedAt
          plan
          id
        }
        memberId
        relationship
        status
        payer {
          id
          code
          name
          memberId
          hint
          bridgeId
        }
        planName
        id
        state
        errors {
          code
          message
        }
      }
      policies {
        latest {
          effectiveTo
          effectiveFrom
          validatedAt
          plan
          id
        }
        memberId
        relationship
        status
        payer {
          id
          code
          name
          memberId
          hint
          bridgeId
        }
        planName
        id
        state
        errors {
          code
          message
        }
      }
      services {
        id
        organizationId
        status
        createdAt
        serviceType {
          selfPayCost
          conditionalPatientResponsibility
          noteFields
          cost
          providerTypes
          providerType
          placeOfService
          name
          id
        }
        dateOfService
        patientId
        location {
          state
        }
        serviceEligibility {
          id
          organizationId
          serviceTypeId
          dateOfService
          status
          providers {
            id
            name
            type
            providerEligibilityEnabled
            serviceEligibilityEnabled
            externalId
          }
          policyIds
          state
          validatedAt
          patientResponsibility {
            total
            fallback
            deductible
            coinsurance
            # coinsurancePercent
            copayment
            oopFallback
            oopCopayment
            oopCoinsurance
            oopDeductible
            deductibleType
          }
          messages
          clinicalInfo {
            diagnoses
          }
          conditionalPatientResponsibilities {
            conditions {
              diagnoses
            }
            limit {
              type
              value
              metric
              period
              units {
                minutes
                min
                max
              }
            }
            patientResponsibility {
              total
              fallback
              deductible
              coinsurance
              # coinsurancePercent
              copayment
              oopFallback
              oopCopayment
              oopCoinsurance
              oopDeductible
              deductibleType
            }
          }
        }
        coverageStatus
        provider {
          id
          name
          type
          providerEligibilityEnabled
          serviceEligibilityEnabled
          externalId
        }
        payments {
          id
          createdAt
          serviceId
          paidAt
          transactionId
          type
          amount
          externalId
          memo
        }
        responsibleParty
        patientResponsibility {
          amount {
            total
            fallback
            deductible
            coinsurance
            # coinsurancePercent
            copayment
            oopFallback
            oopCopayment
            oopCoinsurance
            oopDeductible
            deductibleType
          }
          status
          paid
        }
        tasks {
          type
          memo
        }
        externalId
        noteId
        claimStatus
        priorAuthorizationNumber
      }
      needConsents {
        id
        type
        version
      }
    }
  }
`;
const Patient = ({ bridgePatient }) => {
  if (!bridgePatient) {
    return (
      <Fragment>
        <Flex flex={1} flexDirection="column">
          <Box m={2}>
            <Text color="#344f77" textAlign="left" style={{ fontWeight: 600 }} mb={3}>
              Patient
            </Text>
          </Box>
          <Text color="#344f77" style={{ opacity: 0.65 }}>
            No patient data
          </Text>
        </Flex>
      </Fragment>
    );
  }

  return (
    <Fragment>
      <Flex flex={1} flexDirection="column">
        <Flex m={2}>
          <Text color="#344f77" textAlign="left" style={{ fontWeight: 600 }} mb={3} mr={4}>
            Patient
          </Text>
          <svg
            width="20"
            height="20"
            viewBox="0 0 120 120"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            onClick={() => {
              window.open(`https://paloma.app.usebridge.${isPreviewMode ? 'xyz' : 'com'}/patients/${bridgePatient.id}`, '_blank');
            }}
            style={{ cursor: 'pointer' }}
          >
            <path
              d="M67.5 52.5L105 15M105 15H80M105 15V40M105 70V95C105 97.6522 103.946 100.196 102.071 102.071C100.196 103.946 97.6522 105 95 105H25C22.3478 105 19.8043 103.946 17.9289 102.071C16.0536 100.196 15 97.6522 15 95V25C15 22.3478 16.0536 19.8043 17.9289 17.9289C19.8043 16.0536 22.3478 15 25 15H50"
              stroke="#354E79"
              strokeWidth="10"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </Flex>
        <Text color="#344f77" style={{ opacity: 0.65 }} mb={2}>
          Bridge ID: {bridgePatient.id}
        </Text>
        <Text color="#344f77" style={{ opacity: 0.65 }} mb={2}>
          created on: {bridgePatient.createdAt}
        </Text>
        <Text color="#344f77" style={{ opacity: 0.65 }} mb={2}>
          email: {bridgePatient.email}
        </Text>
        <Text color="#344f77" style={{ opacity: 0.65 }} mb={2}>
          External ID: {bridgePatient.externalId}
        </Text>
      </Flex>
    </Fragment>
  );
};

const Policy = ({ policy, defaultExpanded = false }) => {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);

  return (
    <Card flex={1} p={3} mb={3}>
      <Flex flexDirection={'column'}>
        <Flex flex={1}>
          <Flex flexDirection={'column'} mr={3}>
            <Text fontSize={'12px'} fontWeight={'300'}>
              Plan Name
            </Text>
            <Text>{policy.planName}</Text>
          </Flex>
          <Flex flexDirection={'column'} mr={3}>
            <Text fontSize={'12px'} fontWeight={'300'}>
              Member ID
            </Text>
            <Text>{policy.memberId}</Text>
          </Flex>
          <Flex flexDirection={'column'} mr={3}>
            <Text fontSize={'12px'} fontWeight={'300'}>
              Status
            </Text>
            <Text>{policy.status}</Text>
          </Flex>
          <Box style={{ flex: 1 }} />
          <svg
            width="20"
            height="20"
            viewBox="0 0 120 120"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            onClick={() => {
              window.open(`https://paloma.app.usebridge.${isPreviewMode ? 'xyz' : 'com'}/policies/${policy.id}`, '_blank');
            }}
            style={{ cursor: 'pointer', marginRight: 12 }}
          >
            <path
              d="M67.5 52.5L105 15M105 15H80M105 15V40M105 70V95C105 97.6522 103.946 100.196 102.071 102.071C100.196 103.946 97.6522 105 95 105H25C22.3478 105 19.8043 103.946 17.9289 102.071C16.0536 100.196 15 97.6522 15 95V25C15 22.3478 16.0536 19.8043 17.9289 17.9289C19.8043 16.0536 22.3478 15 25 15H50"
              stroke="#354E79"
              strokeWidth="10"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
          {!isExpanded ? (
            <svg onClick={() => setIsExpanded(!isExpanded)} width="20" height="20" viewBox="0 0 120 120" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M45 25L80 60L45 95" stroke="#354E79" strokeWidth="10" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          ) : (
            <svg onClick={() => setIsExpanded(!isExpanded)} width="20" height="20" viewBox="0 0 120 120" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M56.8201 38.8199C57.6638 37.9772 58.8076 37.5039 60.0001 37.5039C61.1926 37.5039 62.3363 37.9772 63.1801 38.8199L88.6801 64.3199C89.1222 64.7319 89.4768 65.2287 89.7227 65.7807C89.9687 66.3327 90.1009 66.9286 90.1116 67.5328C90.1223 68.137 90.0111 68.7372 89.7848 69.2975C89.5585 69.8579 89.2216 70.3669 88.7943 70.7942C88.367 71.2215 87.858 71.5584 87.2976 71.7847C86.7373 72.011 86.1371 72.1222 85.5329 72.1115C84.9287 72.1008 84.3328 71.9686 83.7808 71.7226C83.2288 71.4767 82.732 71.1221 82.3201 70.6799L60.0001 48.3599L37.6801 70.6799C36.827 71.4748 35.6987 71.9076 34.5329 71.887C33.3671 71.8664 32.2548 71.3942 31.4303 70.5697C30.6058 69.7452 30.1336 68.6329 30.113 67.4671C30.0924 66.3013 30.5252 65.173 31.3201 64.3199L56.8201 38.8199Z"
                fill="#354E79"
              />
            </svg>
          )}
        </Flex>
        {!!isExpanded && (
          <Flex flex={1} p={4} justifyContent={'space-between'}>
            <Flex flexDirection={'column'} mb={3}>
              <Text fontSize={'12px'} fontWeight={'300'}>
                ID
              </Text>
              <Text>{policy.id}</Text>
            </Flex>
            <Flex flexDirection={'column'} mb={3}>
              <Text fontSize={'12px'} fontWeight={'300'}>
                Latest Verification:
              </Text>
              {policy.latest && (
                <>
                  <Text>- Effective from: {policy.latest.effectiveFrom}</Text>
                  <Text>- Effective to: {policy.latest.effectiveTo}</Text>
                  <Text>- ID: {policy.latest.id}</Text>
                  <Text>- plan: {policy.latest.plan}</Text>
                  <Text>- validated At: {policy.latest.validatedAt}</Text>
                </>
              )}
            </Flex>
            <Flex flexDirection={'column'} mb={3}>
              <Text fontSize={'12px'} fontWeight={'300'}>
                Payer:
              </Text>
              {policy.payer && (
                <>
                  <Text>- ID: {policy.payer.id}</Text>
                  <Text>- Code: {policy.payer.code}</Text>
                  <Text>- Name: {policy.payer.name}</Text>
                </>
              )}
            </Flex>
          </Flex>
        )}
      </Flex>
    </Card>
  );
};
const ConsentsNeeded = ({ needConsents }) => {
  if (!needConsents || needConsents.length < 1) {
    return (
      <Fragment>
        <Flex flex={1} flexDirection="column">
          <Box m={2}>
            <Text color="#344f77" textAlign="left" style={{ fontWeight: 600 }} mb={3}>
              Needed consents
            </Text>
          </Box>
          <Text color="#344f77" style={{ opacity: 0.65 }}>
            No consents needed
          </Text>
        </Flex>
      </Fragment>
    );
  }

  return (
    <Fragment>
      <Flex flex={1} flexDirection="column">
        <Flex m={2}>
          <Text color="#344f77" textAlign="left" style={{ fontWeight: 600 }} mb={3} mr={4}>
            Needed consents
          </Text>
        </Flex>

        {needConsents.map((x) => {
          return (
            <Text key={x.id}>
              - {x.type} -- {x.version}
            </Text>
          );
        })}
      </Flex>
    </Fragment>
  );
};

const ActivePolicy = ({ policy }) => {
  if (!policy) {
    return (
      <Fragment>
        <Flex flex={1} flexDirection="column">
          <Box m={2}>
            <Text color="#344f77" textAlign="left" style={{ fontWeight: 600 }} mb={3}>
              Active Policy
            </Text>
          </Box>
          <Text color="#344f77" style={{ opacity: 0.65 }}>
            No active policy
          </Text>
        </Flex>
      </Fragment>
    );
  }

  return (
    <Fragment>
      <Flex flex={1} flexDirection="column">
        <Box m={2}>
          <Text color="#344f77" textAlign="left" style={{ fontWeight: 600 }} mb={3}>
            Active Policy
          </Text>
        </Box>
        <Policy policy={policy} defaultExpanded />
      </Flex>
    </Fragment>
  );
};

const Policies = ({ policies }) => {
  if (!policies || policies.length < 1) {
    return (
      <Fragment>
        <Flex flex={1} flexDirection="column">
          <Box m={2}>
            <Text color="#344f77" textAlign="left" style={{ fontWeight: 600 }} mb={3}>
              Policies
            </Text>
          </Box>
          <Text color="#344f77" style={{ opacity: 0.65 }}>
            No policy
          </Text>
        </Flex>
      </Fragment>
    );
  }

  return (
    <Fragment>
      <Flex flex={1} flexDirection="column">
        <Box m={2}>
          <Text color="#344f77" textAlign="left" style={{ fontWeight: 600 }} mb={3}>
            Policies
          </Text>
        </Box>
        {policies.map((x) => {
          return <Policy key={x.id} policy={x} />;
        })}
      </Flex>
    </Fragment>
  );
};

const Service = ({ service }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  return (
    <Card flex={1} p={3} mb={3}>
      <Flex flexDirection={'column'}>
        <Flex flex={1}>
          <Flex flexDirection={'column'} mr={3}>
            <Text fontSize={'12px'} fontWeight={'300'}>
              Appointment ID
            </Text>
            <Text>{service.externalId}</Text>
          </Flex>
          <Flex flexDirection={'column'} mr={3}>
            <Text fontSize={'12px'} fontWeight={'300'}>
              Date of Service
            </Text>
            <Text>{service.dateOfService}</Text>
          </Flex>
          <Flex flexDirection={'column'} mr={3}>
            <Text fontSize={'12px'} fontWeight={'300'}>
              Status
            </Text>
            <Text>{service.status}</Text>
          </Flex>
          <Flex flexDirection={'column'} mr={3}>
            <Text fontSize={'12px'} fontWeight={'300'}>
              Claim Status
            </Text>
            <Text>{service.claimStatus}</Text>
          </Flex>
          <Flex flexDirection={'column'} mr={3}>
            <Text fontSize={'12px'} fontWeight={'300'}>
              Coverage Status
            </Text>
            <Text>{service.coverageStatus}</Text>
          </Flex>
          <Box style={{ flex: 1 }} />
          <svg
            width="20"
            height="20"
            viewBox="0 0 120 120"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            onClick={() => {
              window.open(`https://paloma.app.usebridge.${isPreviewMode ? 'xyz' : 'com'}/services/${service.id}`, '_blank');
            }}
            style={{ cursor: 'pointer', marginRight: 12 }}
          >
            <path
              d="M67.5 52.5L105 15M105 15H80M105 15V40M105 70V95C105 97.6522 103.946 100.196 102.071 102.071C100.196 103.946 97.6522 105 95 105H25C22.3478 105 19.8043 103.946 17.9289 102.071C16.0536 100.196 15 97.6522 15 95V25C15 22.3478 16.0536 19.8043 17.9289 17.9289C19.8043 16.0536 22.3478 15 25 15H50"
              stroke="#354E79"
              strokeWidth="10"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
          {!isExpanded ? (
            <svg onClick={() => setIsExpanded(!isExpanded)} width="20" height="20" viewBox="0 0 120 120" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M45 25L80 60L45 95" stroke="#354E79" strokeWidth="10" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          ) : (
            <svg onClick={() => setIsExpanded(!isExpanded)} width="20" height="20" viewBox="0 0 120 120" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M56.8201 38.8199C57.6638 37.9772 58.8076 37.5039 60.0001 37.5039C61.1926 37.5039 62.3363 37.9772 63.1801 38.8199L88.6801 64.3199C89.1222 64.7319 89.4768 65.2287 89.7227 65.7807C89.9687 66.3327 90.1009 66.9286 90.1116 67.5328C90.1223 68.137 90.0111 68.7372 89.7848 69.2975C89.5585 69.8579 89.2216 70.3669 88.7943 70.7942C88.367 71.2215 87.858 71.5584 87.2976 71.7847C86.7373 72.011 86.1371 72.1222 85.5329 72.1115C84.9287 72.1008 84.3328 71.9686 83.7808 71.7226C83.2288 71.4767 82.732 71.1221 82.3201 70.6799L60.0001 48.3599L37.6801 70.6799C36.827 71.4748 35.6987 71.9076 34.5329 71.887C33.3671 71.8664 32.2548 71.3942 31.4303 70.5697C30.6058 69.7452 30.1336 68.6329 30.113 67.4671C30.0924 66.3013 30.5252 65.173 31.3201 64.3199L56.8201 38.8199Z"
                fill="#354E79"
              />
            </svg>
          )}
        </Flex>
        {!!isExpanded && (
          <Flex flex={1} p={4} flexDirection={'column'}>
            <Flex flexDirection={'column'} mb={3}>
              <Text fontSize={'12px'} fontWeight={'300'}>
                ID
              </Text>
              <Text>{service.id}</Text>
            </Flex>
            <Flex flexDirection={'column'} mb={3}>
              <Text fontSize={'12px'} fontWeight={'300'}>
                Prior Authorization
              </Text>
              <Text>{service.priorAuthorizationNumber}</Text>
            </Flex>
            <Flex flexDirection={'column'} mb={3}>
              <Text fontSize={'12px'} fontWeight={'300'}>
                Responsible Party
              </Text>
              <Text>{service.responsibleParty}</Text>
            </Flex>
            <Flex flexDirection={'column'} mb={3}>
              <Text fontSize={'12px'} fontWeight={'300'}>
                Patient Responsibility:
              </Text>
              <Flex flexDirection={'column'} px={3}>
                {service.patientResponsibility && (
                  <>
                    <Text>- Status: {service.patientResponsibility.status}</Text>
                    {service.patientResponsibility.amount && (
                      <>
                        <Text>- Coinsurance: {service.patientResponsibility.amount.copayment ? service.patientResponsibility.amount.coinsurance / 100 : 0}</Text>
                        <Text>- Deductible: {service.patientResponsibility.amount.copayment ? service.patientResponsibility.amount.deductible / 100 : 0}</Text>
                        <Text>- Fallback: {service.patientResponsibility.amount.copayment ? service.patientResponsibility.amount.fallback / 100 : 0}</Text>
                        <Text>- Copayment: {service.patientResponsibility.amount.copayment ? service.patientResponsibility.amount.copayment / 100 : 0}</Text>
                        <Text>- Total: {service.patientResponsibility.amount.copayment ? service.patientResponsibility.amount.total / 100 : 0}</Text>
                      </>
                    )}
                  </>
                )}
              </Flex>
            </Flex>
            <Flex flexDirection={'column'} mb={3}>
              <Text fontSize={'12px'} fontWeight={'300'}>
                Provider:
              </Text>
              <Flex flexDirection={'column'} px={3}>
                {service.provider && (
                  <>
                    <Text>- Name: {service.provider.name}</Text>
                  </>
                )}
              </Flex>
            </Flex>
            <Flex flexDirection={'column'} mb={3}>
              <Text fontSize={'12px'} fontWeight={'300'}>
                Payments:
              </Text>

              {service.payments && (
                <>
                  {service.payments.map((x) => {
                    return (
                      <Card key={x.id} p={2}>
                        <Flex justifyContent={'space-between'}>
                          <Text fontSize={'12px'} fontWeight={'300'}>
                            Type
                          </Text>
                          <Text>{x.type}</Text>
                          <Text fontSize={'12px'} fontWeight={'300'}>
                            Paid At
                          </Text>
                          <Text>{x.paidAt}</Text>
                          <Text fontSize={'12px'} fontWeight={'300'}>
                            Amount
                          </Text>
                          <Text>${(x.amount || 0) / 100}</Text>
                          <Text fontSize={'12px'} fontWeight={'300'}>
                            Transaction ID
                          </Text>
                          <Text>{x.transactionId}</Text>
                        </Flex>
                        <Text mt={3} fontSize={'12px'} fontWeight={'300'}>
                          Memo
                        </Text>
                        <Text>{x.memo}</Text>
                      </Card>
                    );
                  })}
                </>
              )}
            </Flex>
          </Flex>
        )}
      </Flex>
    </Card>
  );
};
const Services = ({ services }) => {
  if (!services || services.length < 1) {
    return (
      <Fragment>
        <Flex flex={1} flexDirection="column">
          <Box m={2}>
            <Text color="#344f77" textAlign="left" style={{ fontWeight: 600 }} mb={3}>
              Services
            </Text>
          </Box>
          <Text color="#344f77" style={{ opacity: 0.65 }}>
            No services
          </Text>
        </Flex>
      </Fragment>
    );
  }

  return (
    <Fragment>
      <Flex flex={1} flexDirection="column">
        <Box m={2}>
          <Text color="#344f77" textAlign="left" style={{ fontWeight: 600 }} mb={3}>
            Services
          </Text>
        </Box>
        {services.map((x) => {
          return <Service key={x.id} service={x} />;
        })}
      </Flex>
    </Fragment>
  );
};
// userId: db user id
export const BridgeView = ({ userId, close }) => {
  const escFunction = useCallback((event) => {
    if (event.key === 'Escape') {
      event.stopPropagation();

      close();
    } else {
    }
  }, []);

  useEffect(
    () => {
      document.addEventListener('keydown', escFunction, true);

      return () => {
        document.removeEventListener('keydown', escFunction, true);
      };
    },
    [escFunction]
  );
  return (
    <Container>
      <Query query={GET_BRIDGE_DATA} variables={{ userId }}>
        {({ loading, data }) => {
          if (loading) {
            return (
              <Fragment>
                <Flex flex={1} flexDirection="column">
                  <Box m={2}>
                    <Heading color="#344f77">Bridge Data</Heading>
                  </Box>
                  <PleaseWait />
                </Flex>
              </Fragment>
            );
          }
          if (!data || !data.getUserBridgeData) {
            return (
              <Fragment>
                <Flex flex={1} flexDirection="column">
                  <Box m={2}>
                    <Heading color="#344f77">Bridge Data</Heading>
                  </Box>
                  <Text color="#344f77" style={{ opacity: 0.65 }}>
                    No data
                  </Text>
                  <Button
                    variant={'pink'}
                    style={{
                      padding: '4px 12px',
                      alignSelf: 'center',
                    }}
                    onClick={close}
                  >
                    Close
                  </Button>
                </Flex>
              </Fragment>
            );
          }
          const d = data.getUserBridgeData;
          console.log({ d });
          return (
            <Flex flexDirection={'column'}>
              <Flex flex={1} flexDirection="column" style={{ overflowY: 'auto', flex: 1 }}>
                <Box m={2}>
                  <Heading color="#344f77">Bridge Data</Heading>
                </Box>
                <Patient bridgePatient={d.bridgePatient} />
                <ConsentsNeeded needConsents={d.needConsents} />
                <ActivePolicy policy={d.policy} />
                <Policies policies={d.policies} />
                <Services services={d.services} />
              </Flex>
              <Button
                variant={'pink'}
                style={{
                  padding: '4px 12px',
                  alignSelf: 'center',
                }}
                onClick={close}
              >
                Close
              </Button>
            </Flex>
          );
        }}
      </Query>
    </Container>
  );
};
